import type { RouteLocationNormalized, RouteRecordName } from 'vue-router'

export type OrganizationRouteDestination = 'application' | 'setup-brand' | 'setup-blocked'

type OrganizationRoute = Pick<RouteLocationNormalized, 'meta' | 'name' | 'params' | 'redirectedFrom'>

const organizationIdOf = (route: Pick<OrganizationRoute, 'params'>) => {
  const organizationId = route.params.organizationId
  return typeof organizationId === 'string' ? organizationId : undefined
}

export const organizationRouteDestination = (hasBrands: boolean, mayCreateBrand: boolean): OrganizationRouteDestination => {
  if (hasBrands) return 'application'
  return mayCreateBrand ? 'setup-brand' : 'setup-blocked'
}

export const staleSetupDestination = (
  route: Pick<OrganizationRoute, 'name' | 'params'>,
  hasBrands: boolean
): RouteRecordName | undefined => {
  if (hasBrands && route.name === 'setup-brand' && typeof route.params.brandId !== 'string') return 'brand-new'
}

export const organizationEntryRedirect = (
  route: Pick<OrganizationRoute, 'meta' | 'name' | 'params'>,
  hasBrands: boolean,
  mayCreateBrand: boolean
): RouteRecordName | undefined => {
  const staleDestination = staleSetupDestination(route, hasBrands)
  if (staleDestination) return staleDestination

  if (route.meta.setup === true && typeof route.params.brandId === 'string') return

  const destination = organizationRouteDestination(hasBrands, mayCreateBrand)
  if (destination === 'application') return route.meta.setup === true ? 'dashboard' : undefined
  if (route.name === destination) return
  return destination
}

const followsOrganizationRoutingRedirect = (to: OrganizationRoute) => {
  const redirectedFrom = to.redirectedFrom
  if (!redirectedFrom || organizationIdOf(to) !== organizationIdOf(redirectedFrom)) return false

  const enteredSetup = redirectedFrom.meta.setup !== true && (to.name === 'setup-brand' || to.name === 'setup-blocked')
  const leftStaleSetup = redirectedFrom.name === 'setup-brand' && to.name === 'brand-new'
  const changedSetupOutcome =
    (redirectedFrom.name === 'setup-brand' && to.name === 'setup-blocked') ||
    (redirectedFrom.name === 'setup-blocked' && to.name === 'setup-brand')
  return enteredSetup || leftStaleSetup || changedSetupOutcome
}

export const organizationRouteNeedsRead = (to: OrganizationRoute, from: OrganizationRoute) => {
  const organizationId = organizationIdOf(to)
  if (!organizationId || followsOrganizationRoutingRedirect(to)) return false

  const redirectedFrom = to.redirectedFrom
  if (redirectedFrom?.name === 'organization' && organizationId === organizationIdOf(redirectedFrom)) return true

  const sourceOrganizationId = organizationIdOf(from)
  if (!sourceOrganizationId || sourceOrganizationId !== organizationId) return true
  if (to.name === 'setup-brand' && typeof to.params.brandId !== 'string') return true
  if (from.meta.setup === true && to.meta.setup !== true) return true

  return false
}
